// src/hooks/useAuth.js
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useAuthStore, { AuthUser } from '../store/authStore';
import axios from 'axios';
import { useEffect } from 'react';
import apiClient from '@/api/_axios';

interface AuthStore {
   token: string | null;
   user: AuthUser | null;
   isAuthenticated: boolean;
   setAuth: (token: string, user: AuthUser) => void;
   clearAuth: () => void;
}

const verifyToken = async (token: string): Promise<AuthUser> => {
   if (!token) throw new Error('No token');
   const { data } = await apiClient.get<AuthUser>('/auth/is-auth', {
      headers: { Authorization: `Bearer ${token}` },
   });
   return data;
};

export const useAuth = () => {
   const { token, setAuth, clearAuth } = useAuthStore() as AuthStore;
   const queryClient = useQueryClient();
   const {
      isLoading,
      error,
      data: userData,
   } = useQuery<AuthUser, Error>({
      queryKey: ['auth', token],
      queryFn: () =>
         token ? verifyToken(token) : Promise.reject(new Error('No token')),
      enabled: !!token,
      retry: false,
   });

   useEffect(() => {
      if (token && userData) {
         setAuth(token, userData);
      }
   }, [token, userData, setAuth]);

   useEffect(() => {
      if (error) {
         clearAuth();
         queryClient.invalidateQueries({ queryKey: ['auth'] });
      }
   }, [error, clearAuth, queryClient]);

   const clearAuthWithCache = () => {
      clearAuth();
      queryClient.invalidateQueries({ queryKey: ['auth'] });
   };

   // `userData` is the FRESH server answer; the store copy can still be the
   // stale one from localStorage for a render or two (setAuth runs in the
   // effect above). Callers that gate on `verified` must prefer this one, or
   // a returning verified user flashes the verify screen on every load.
   return { isLoading, error, clearAuthWithCache, userData };
};
